import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { HreflangTags } from "@/components/HreflangTags";
import { LanguageRedirect } from "@/components/LanguageRedirect";
import { URLRedirectHandler } from "@/components/URLRedirectHandler";
import CartDrawer from "@/components/CartDrawer";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminRoute } from "@/components/AdminRoute";
import { lazy, Suspense } from "react";
import { useCartSync } from "@/hooks/useCartSync";

// Lazy load route components for better code splitting
const Index = lazy(() => import("./pages/Index"));
const Product = lazy(() => import("./pages/Product"));
const Admin = lazy(() => import("./pages/Admin"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Account = lazy(() => import("./pages/Account"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const Compare = lazy(() => import("./pages/Compare"));
const OrderSuccess = lazy(() => import("./pages/OrderSuccess"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const BlogCategory = lazy(() => import("./pages/BlogCategory"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Shipping = lazy(() => import("./pages/Shipping"));
const Returns = lazy(() => import("./pages/Returns"));
const Payment = lazy(() => import("./pages/Payment"));
const TermsOfSale = lazy(() => import("./pages/TermsOfSale"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Legal = lazy(() => import("./pages/Legal"));
const AmazonPrivacy = lazy(() => import("./pages/AmazonPrivacy"));
const NotFound = lazy(() => import("./pages/NotFound"));
const LandingPage = lazy(() => import("./pages/LandingPage"));

const queryClient = new QueryClient();

// Component to initialize cart sync
const CartSyncInitializer = () => {
  useCartSync();
  return null;
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <CartSyncInitializer />
              <HreflangTags />
              <LanguageRedirect />
              <URLRedirectHandler />
              <CartDrawer />
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                <Routes>
                  {/* Redirections SEO pour anciennes URLs */}
                  <Route path="/shop" element={<Navigate to="/fr" replace />} />
                  <Route path="/product/:id" element={<Navigate to="/fr" replace />} />
                  
                  {/* Routes avec préfixe langue */}
                  <Route path="/:lang">
                    <Route index element={<Index />} />
                    <Route path="produits/:slug" element={<Product />} />
                    <Route path="blog" element={<Blog />} />
                    <Route path="blog/category/:category" element={<BlogCategory />} />
                    <Route path="blog/:slug" element={<BlogPost />} />
                    <Route path="about" element={<About />} />
                    <Route path="contact" element={<Contact />} />
                    
                    <Route path="shipping" element={<Shipping />} />
                    <Route path="returns" element={<Returns />} />
                    <Route path="payment" element={<Payment />} />
                    <Route path="terms-of-sale" element={<TermsOfSale />} />
                    <Route path="terms-of-use" element={<TermsOfUse />} />
                    <Route path="privacy" element={<Privacy />} />
                    <Route path="amazon-privacy" element={<AmazonPrivacy />} />
                    <Route path="legal" element={<Legal />} />
                    <Route path="wishlist" element={<Wishlist />} />
                    <Route path="compare" element={<Compare />} />
                    <Route path="checkout" element={<Checkout />} />
                    <Route path="order-success" element={<OrderSuccess />} />
                    <Route path="auth" element={<Auth />} />
                    <Route path="reset-password" element={<ResetPassword />} />
                    <Route path="account" element={<Account />} />
                    <Route path="collection/:slug" element={<LandingPage />} />
                  </Route>
                  
                  {/* Admin sans préfixe langue */}
                  <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
                  
                  {/* 404 */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </LanguageProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
